import type { YesNo } from "@/types/utils";
import {
  type ChoiceBasicQuestionQuestionType,
  type Condition,
  NumericalRule,
  QuestionType,
  type QuestionWithValidationRules,
  type ReviewPageBasicQuestion,
  type ReviewPageBasicQuestionPayload,
  type ReviewPageDraftQuestion,
  type SimpleBasicQuestionQuestionType,
} from "@/types/screener-builder";
import { valueIncludedOrEqual } from "@/utils/helpers";
import { isEmptyAfterTrim } from "@/utils/string";

export const POST_PUBLISH_EDIT_WARNINGS = {
  answerMismatch:
    "If you update a published question, past answers might no longer match what’s being asked. To avoid confusion, create a new question for significant changes.",
  definitionRescoring:
    "Changes to the competency definition will affect scoring for new responses. Existing responses will not be re-scored retroactively.",
  qualifyingRescoring:
    "Changes to qualifying conditions will apply to new responses only. Existing responses will not be re-scored retroactively.",
  rubric:
    "Changes to the scoring rubric will apply to new responses only. Existing responses will not be re-scored retroactively.",
} as const;

export const questionTypeTypeGuard = <T extends QuestionType>(
  questionType: T,
  question: ReviewPageDraftQuestion,
): question is ReviewPageDraftQuestion<T> =>
  question.questionType === questionType;

export const existingQuestionTypeGuard = (
  question: ReviewPageDraftQuestion,
): question is ReviewPageBasicQuestion => Object.hasOwn(question, "id");

export const isChoiceQuestion = (
  question: ReviewPageDraftQuestion,
): question is ReviewPageDraftQuestion<
  QuestionType.MultipleChoice | QuestionType.SingleChoice
> =>
  valueIncludedOrEqual(
    [QuestionType.MultipleChoice, QuestionType.SingleChoice],
    question.questionType,
  );

export const isQualifyingEnabledQuestion = (
  question: ReviewPageDraftQuestion,
): question is QuestionWithValidationRules =>
  valueIncludedOrEqual(
    [QuestionType.Number, QuestionType.SingleChoice, QuestionType.YesNo],
    question.questionType,
  );

export const formatClosingPhraseError = (closingPhrase: string) =>
  `Question cannot contain "${closingPhrase}"`;

export const removeQualifying = <T extends ReviewPageDraftQuestion>(
  question: T,
): T => ({
  ...question,
  isQualifying: false,
  validationRules: undefined,
});

export const hasValidationRules = (
  question: ReviewPageDraftQuestion,
): question is QuestionWithValidationRules => {
  return (
    "validationRules" in question &&
    question.validationRules !== null &&
    question.validationRules !== undefined
  );
};

export const getChoiceRules = (
  question: ReviewPageDraftQuestion,
): Condition[] | undefined => {
  if (!hasValidationRules(question)) {
    return;
  }

  const rules = question.validationRules;
  if (rules && "choiceRules" in rules && rules.choiceRules) {
    return rules.choiceRules;
  }
};

export const getYesNoRule = (
  question: ReviewPageDraftQuestion,
): YesNo | undefined => {
  if (!hasValidationRules(question)) {
    return;
  }

  const rules = question.validationRules;
  if (rules && "yesNoRule" in rules) {
    return rules.yesNoRule;
  }
};

export const OPERATOR_MAP: { [key: string]: string } = {
  "=": "equal to",
  "<": "less than",
  ">": "greater than",
  "!=": "not equal to",
  ">=": "greater than or equal to",
  "<=": "less than or equal to",
};

const ruleMap: { [key: string]: NumericalRule } = {
  "=": NumericalRule.EqualTo,
  "<": NumericalRule.LessThan,
  ">": NumericalRule.GreaterThan,
  "!=": NumericalRule.NotEqualTo,
  ">=": NumericalRule.GreaterThanOrEqualTo,
  "<=": NumericalRule.LessThanOrEqualTo,
};

export const parseNumericalRule = (rule: string | undefined) => {
  if (!rule) {
    return null;
  }

  const regex = /^([<>=!]{1,2})(-?\d*\.?\d+)$/;
  const match = rule.match(regex);
  if (!match) {
    return null;
  }

  const operatorStr = match[1];
  const valueStr = match[2];
  const condition = ruleMap[operatorStr];
  if (condition === undefined) {
    return null;
  }

  return { condition, value: valueStr };
};

export const getNumericalRule = (
  question: ReviewPageDraftQuestion,
): string | undefined => {
  if (!hasValidationRules(question)) return undefined;

  const rules = question.validationRules;
  if (rules && "numericalRule" in rules) {
    return rules.numericalRule;
  }

  return undefined;
};

export const getQualifyingChoiceConditions = (
  question: ReviewPageDraftQuestion,
): Condition[] => {
  const choiceRules = getChoiceRules(question);
  if (!choiceRules) return [];

  return choiceRules.filter(
    (rule) =>
      typeof rule.qualifier === "string" && !isEmptyAfterTrim(rule.qualifier),
  );
};

export const getChoiceOptionTexts = (
  question: ReviewPageDraftQuestion,
): string[] | undefined => {
  const choiceRules = getChoiceRules(question);
  return choiceRules?.map((option) => option.optionText);
};

// TypeScript was having trouble narrowing from a generic type and a switch
// statement. Fell back to overloads as an alternative
export function getDraftBasicQuestion(
  questionType: QuestionType.Number,
): ReviewPageBasicQuestionPayload<QuestionType.Number>;
export function getDraftBasicQuestion(
  questionType: QuestionType.YesNo,
): ReviewPageBasicQuestionPayload<QuestionType.YesNo>;
export function getDraftBasicQuestion(
  questionType: ChoiceBasicQuestionQuestionType,
): ReviewPageBasicQuestionPayload<ChoiceBasicQuestionQuestionType>;
export function getDraftBasicQuestion(
  questionType: SimpleBasicQuestionQuestionType,
): ReviewPageBasicQuestionPayload<SimpleBasicQuestionQuestionType>;
export function getDraftBasicQuestion(
  questionType: QuestionType,
): ReviewPageBasicQuestionPayload;
export function getDraftBasicQuestion(
  questionType: QuestionType,
): ReviewPageBasicQuestionPayload {
  const baseQuestion: Omit<
    ReviewPageBasicQuestionPayload,
    "questionType" | "validationRules"
  > = {
    internalLabel: "",
    isIncluded: true,
    isQualifying: false,
    questionText: "",
    showInSummary: false,
  };

  switch (questionType) {
    case QuestionType.Number:
      return {
        ...baseQuestion,
        questionType: QuestionType.Number,
        validationRules: {
          numericalRule: `${NumericalRule.EqualTo}0`,
        },
      };
    case QuestionType.SingleChoice:
    case QuestionType.MultipleChoice:
    case QuestionType.YesNo:
    case QuestionType.Email:
    case QuestionType.Name:
    case QuestionType.Phone:
    case QuestionType.ShortAnswer:
    case QuestionType.Voice:
    case QuestionType.FileUpload:
      return {
        ...baseQuestion,
        questionType,
        validationRules: null,
      };
    default:
      throw new Error(`Unsupported question type: ${questionType}`);
  }
}
